import React, { ReactNode } from 'react';
import { blueGrey } from '@mui/material/colors';
import { useCountries, useRegions } from 'modules/geo';
import { useFormContext, useWatch } from 'react-hook-form';
import { Box, Stack, Typography, Alert } from '@mui/material';
import { convertEnumToSelectOptions } from 'utils/selectOptionsHelper';
import { AutocompleteValueField, ChipsInputField } from 'components/form';
import { AddLocation, LocalShipping, PeopleAlt } from '@mui/icons-material';
import { BizOrIndividual, PaymentType, TruckType, Country } from '@trnsact/trnsact-shared-types';

import { DealSheetFormValues } from './types';

export const RulesSection = () => {
  const { control, formState } = useFormContext<DealSheetFormValues>();
  const countryValue = useWatch({ control, name: 'address.country' });

  const { countries } = useCountries();
  const { regions } = useRegions(countryValue);

  const countryDependedFields: Record<Country, Record<'code' | 'region', ReactNode>> = {
    [Country.Us]: {
      code: <ChipsInputField name="address.zip" label="ZIP Code" control={control} />,
      region: (
        <AutocompleteValueField
          multiple
          size="small"
          control={control}
          options={regions}
          name="address.state"
          disableCloseOnSelect
          textFieldProps={{ label: 'State' }}
        />
      ),
    },
    [Country.Ca]: {
      code: <ChipsInputField name="address.postalCode" label="Postal Code" control={control} />,
      region: (
        <AutocompleteValueField
          multiple
          size="small"
          options={regions}
          control={control}
          disableCloseOnSelect
          name="address.province"
          textFieldProps={{ label: 'Province' }}
        />
      ),
    },
  };

  return (
    <Stack gap="1rem">
      <Alert severity={formState.errors.root ? 'error' : 'info'} sx={{ marginTop: '0.5rem' }}>
        <Typography>
          Create business rules by specifying one or more criteria. At least one field must be selected
        </Typography>
      </Alert>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <PeopleAlt />

          <Typography sx={{ color: blueGrey['500'] }}>Customer Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <AutocompleteValueField
            multiple
            size="small"
            control={control}
            name="customer.type"
            disableCloseOnSelect
            textFieldProps={{ label: 'Customer type' }}
            options={[
              { label: 'Individual', value: BizOrIndividual.Individual },
              { label: 'Business', value: BizOrIndividual.Business },
            ]}
          />

          <AutocompleteValueField
            multiple
            size="small"
            control={control}
            disableCloseOnSelect
            name="customer.paymentType"
            textFieldProps={{ label: 'Payment type' }}
            options={convertEnumToSelectOptions(PaymentType)}
          />

          <Box />
        </Box>
      </Stack>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <AddLocation />

          <Typography sx={{ color: blueGrey['500'] }}>Location Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <AutocompleteValueField
            size="small"
            control={control}
            options={countries}
            name="address.country"
            textFieldProps={{ label: 'Country' }}
          />

          {countryDependedFields[countryValue].code}

          <Box />

          {countryDependedFields[countryValue].region}

          <ChipsInputField name="address.county" control={control} label="County" />

          <ChipsInputField name="address.city" control={control} label="City" />
        </Box>
      </Stack>

      <Stack gap="1rem">
        <Box
          sx={{
            gap: '0.3rem',
            display: 'flex',
            borderRadius: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: blueGrey['50'],
          }}
        >
          <LocalShipping />

          <Typography sx={{ color: blueGrey['500'] }}>Equipment Configuration</Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
          <AutocompleteValueField
            multiple
            size="small"
            control={control}
            disableCloseOnSelect
            name="equipment.type"
            textFieldProps={{ label: 'Equipment type' }}
            options={convertEnumToSelectOptions(TruckType)}
          />

          <Box />

          <Box />
        </Box>
      </Stack>
    </Stack>
  );
};
